import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ y: 24, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

export const LineReveal = ({ lines, className = "", as: Tag = "h1", delayStep = 0.14, startDelay = 0.15 }) => (
  <Tag className={className}>
    {lines.map((line, i) => (
      <span className="mask-line" key={i}>
        <motion.span
          className="block"
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: startDelay + i * delayStep }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Marquee = ({ items }) => {
  const row = items.map((item, i) => (
    <span key={i} className="flex items-center gap-6 pr-6 shrink-0">
      <span className="font-serif italic text-lg sm:text-xl text-ink-muted whitespace-nowrap">{item}</span>
      <span className="w-1.5 h-1.5 rounded-full bg-terra/50" aria-hidden="true" />
    </span>
  ));
  return (
    <div className="overflow-hidden border-y border-ink/[0.06] py-5 bg-warm/60" aria-hidden="true">
      <div className="marquee-track">
        {row}
        {row}
      </div>
    </div>
  );
};

export const FloatingOrbs = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    <div className="absolute -top-24 -right-16 w-[26rem] h-[26rem] rounded-full bg-terra-light blur-3xl opacity-70 animate-drift-slow" />
    <div className="absolute top-1/3 -left-24 w-[22rem] h-[22rem] rounded-full bg-sage-light blur-3xl opacity-80 animate-drift-slow" style={{ animationDelay: "-6s" }} />
    <div className="absolute bottom-0 right-1/4 w-[18rem] h-[18rem] rounded-full bg-gold/10 blur-3xl animate-drift-slow" style={{ animationDelay: "-3s" }} />
  </div>
);

export const ParallaxImage = ({ src, alt, className = "", speed = 0.12 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}%`, `${speed * 100}%`]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-[120%] object-cover" loading="lazy" />
    </div>
  );
};

export const Chapter = ({ number, title, children }) => (
  <Reveal className="grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-10 items-start">
    <span className="font-serif text-5xl sm:text-6xl font-light text-terra/40 leading-none select-none" aria-hidden="true">
      {number}
    </span>
    <div>
      <h3 className="font-serif text-2xl sm:text-3xl font-medium text-ink mb-4 leading-snug">{title}</h3>
      <div className="text-ink-soft leading-relaxed text-base sm:text-lg space-y-4">{children}</div>
    </div>
  </Reveal>
);
